import { t, locale } from '../i18n';
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, Tag, User } from 'lucide-react';
import { BlogPost as BlogPostData } from '../context/BlogContext';
import { useLocalizedBlog as useBlog } from '../hooks/useLocalizedContent';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getImageUrl } from '../utils/imageUtils';
import { getSeoForPath } from '../data/seo';
import { usePageSeo } from '../hooks/usePageSeo';

const sanitizeMarkdownContent = (value: string) =>
  value
    .replace(/\[[^\]]*]\(\/blog\/undefined\)/gi, '')
    .replace(/\/blog\/undefined/gi, '')
    .split('\n')
    .filter((line) => !/^\s*[—–-]\s*and\s*[—–-]\s*\[\]\s*$/i.test(line.trim()))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { getPostBySlug, posts, loading } = useBlog();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageSeo = useMemo(() => {
    const routeSeo = getSeoForPath(location.pathname);
    if (!post) return routeSeo;

    return {
      ...routeSeo,
      title: `${post.title} | Virtuoz Solutions Blog`,
      description: post.excerpt,
      type: 'Article' as const,
      image: post.image,
      tags: post.tags,
    };
  }, [location.pathname, post]);

  usePageSeo(pageSeo);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      try {
        setIsLoading(true);
        const fetchedPost = await getPostBySlug(slug);
        setPost(fetchedPost ?? null);

        if (!fetchedPost) {
          if (posts.length > 0) {
            navigate('/blog', { replace: true });
          }
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError(t("Failed to load blog post"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
    window.scrollTo(0, 0);
  }, [slug, getPostBySlug, navigate, posts.length]);

  if (isLoading || loading) {
    return (
      <div className="max-w-4xl py-12 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-card w-1/3 mx-auto mb-4 rounded"></div>
          <div className="h-4 bg-card w-1/4 mx-auto mb-8 rounded"></div>
          <div className="h-64 bg-card w-full mx-auto rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-4xl py-12 text-center">
        <h2 className="text-2xl font-bold mb-4">{t("Unable to load blog post")}</h2>
        <p className="text-foreground/70 mb-8">{error || t("Blog post not found")}</p>
        <Link to="/blog" className="btn btn-primary">
          {t("Return to Blog")}
        </Link>
      </div>
    );
  }

  const { title, content, date, author, tags, readTime, image, categories } = post;
  const safeContent = sanitizeMarkdownContent(content || '');

  const formattedDate = new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const relatedPosts = posts
    .filter(p =>
      p.id !== post.id &&
      p.categories.some(cat => post.categories.includes(cat))
    )
    .slice(0, 3);

  return (
    <div className="w-full">
      {/* Header */}
      <section className="max-w-5xl py-8 md:py-12">
        <Link to="/blog" className="inline-flex items-center text-foreground/70 hover:text-foreground mb-6 group transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          {t("Back to all articles")}
        </Link>

        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map((category, index) => (
            <span
              key={index}
              className="bg-primary/10 text-primary text-sm px-3 py-1 rounded"
            >
              {category}
            </span>
          ))}
        </div>

        <h1 className="font-display text-fluid-3xl mb-6">{title}</h1>

        <div className="flex flex-wrap gap-6 text-foreground/60 text-sm">
          <div className="flex items-center">
            <User className="w-4 h-4 mr-2" />
            {author}
          </div>
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            {formattedDate}
          </div>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            {readTime} {t("min read")}
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {image && (
        <section className="py-4">
          <div className="aspect-[16/8] overflow-hidden rounded-lg bg-foreground/5">
            <img
              src={getImageUrl(image)}
              alt={title}
              className="w-full h-full object-cover"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-8">
        <div className="grid grid-cols-1 gap-10 xl:grid-cols-[minmax(0,860px)_340px] xl:items-start 2xl:grid-cols-[minmax(0,920px)_380px] 2xl:gap-14">
          <div className="min-w-0">
            <article className="blog-content" lang={post.contentLanguage || post.sourceLanguage}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h2>{children}</h2>,
                  a: ({ href, children }) => {
                    if (href?.startsWith('/blog/') && !href.includes('undefined')) {
                      return <Link to={href}>{children}</Link>;
                    }

                    return (
                      <a
                        href={href}
                        target={href?.startsWith('http') ? '_blank' : undefined}
                        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {safeContent}
              </ReactMarkdown>
            </article>

            {/* Tags */}
            <div className="mt-12 pt-6 border-t border-foreground/10">
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="w-5 h-5 text-foreground/50" />
                {tags.map((tag, index) => (
                  <Link
                    key={index}
                    to={`/blog?tag=${tag}`}
                    className="bg-card border border-foreground/10 hover:border-primary/30 text-foreground/70 px-3 py-1 rounded-lg text-sm transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside>
            <div className="space-y-6 xl:sticky xl:top-8">
              <div className="rounded-lg border border-foreground/10 bg-card p-6">
                <h3 className="font-bold mb-4">{t("About the Author")}</h3>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold">{author}</h4>
                    <p className="text-foreground/60 text-sm">{t("Developer & IT Professional")}</p>
                  </div>
                </div>
                <p className="text-foreground/60 text-sm">
                  {t("A passionate developer with over a decade of experience in creating innovative solutions.")}
                </p>
              </div>

              {relatedPosts.length > 0 && (
                <div className="rounded-lg border border-foreground/10 bg-card p-6">
                  <h3 className="font-bold mb-4">{t("Related Articles")}</h3>
                  <div className="space-y-4">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.slug}`}
                        className="block group"
                      >
                        <div className="flex gap-4">
                          <div className="w-16 h-12 overflow-hidden rounded-lg flex-shrink-0 bg-foreground/5">
                            <img
                              src={getImageUrl(relatedPost.image)}
                              alt={relatedPost.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2">
                              {relatedPost.title}
                            </h4>
                            <p className="text-foreground/50 text-xs mt-1">
                              {new Date(relatedPost.date).toLocaleDateString(locale, {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
